// apps/admin-ui/src/components/widgets/AttestationStatus.tsx
// Monthly attestation status widget for CPA auditors
// ALL RIGHTS RESERVED

import '../styles/widgets.css';

export interface AttestationStatusProps {
  data?: {
    lastAttestation: string;
    status: 'current' | 'due' | 'overdue';
    nextDue: string;
    reserveTotal: number;
    circulationTotal: number;
    ratio: number;
  };
}

export function AttestationStatus({ data }: AttestationStatusProps) {
  // Mock data for development
  const attestation = data || {
    lastAttestation: '2026-01-15',
    status: 'current' as const,
    nextDue: '2026-02-15',
    reserveTotal: 125_000_000,
    circulationTotal: 61_000_000,
    ratio: 2.05,
  };

  const statusColors = {
    current: 'var(--color-success)',
    due: 'var(--color-warning)',
    overdue: 'var(--color-error)',
  };

  const statusLabels = {
    current: '✓ Current',
    due: '◐ Due Soon',
    overdue: '✗ Overdue',
  };

  const formatCurrency = (value: number) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(value);

  const formatDate = (dateStr: string) =>
    new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

  return (
    <div className="widget-attestation">
      <div className="attestation-header">
        <span
          className="attestation-status"
          style={{ color: statusColors[attestation.status] }}
        >
          {statusLabels[attestation.status]}
        </span>
        <span className="attestation-date">
          Last: {formatDate(attestation.lastAttestation)}
        </span>
      </div>

      <div className="attestation-metrics">
        <div className="metric-row">
          <span className="metric-label">Reserve Total</span>
          <span className="metric-value">{formatCurrency(attestation.reserveTotal)}</span>
        </div>
        <div className="metric-row">
          <span className="metric-label">In Circulation</span>
          <span className="metric-value">{formatCurrency(attestation.circulationTotal)}</span>
        </div>
        <div className="metric-row">
          <span className="metric-label">Reserve Ratio</span>
          <span className="metric-value">{attestation.ratio.toFixed(2)}:1</span>
        </div>
      </div>

      <div className="attestation-footer">
        <span className="next-due">
          Next attestation due: <strong>{formatDate(attestation.nextDue)}</strong>
        </span>
      </div>
    </div>
  );
}

export default AttestationStatus;
